import { GridColDef, GridColumnGroup } from "@mui/x-data-grid-premium";
import React from "react";

import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import {
  MixGrouping,
  MixMaterial,
  SingleMixSummary,
} from "src/store/api/generatedApi";

import { useTenantData } from "contexts/tenant";
import { Period } from "hooks/periodIndex";
import { useFormatNumber } from "src/utils/formatNumber";
import { PlanId } from "../boundary/PlanId";
import {
  basketWeight,
  copperGroup,
  driWeight,
  material as materialColumn,
  mixSummary,
  numHeats,
  plotSelectSteelGrades,
  subStandardGroup,
} from "./columns";
import { useFilterModel } from "./filterModel";
import { Detail } from "./MixSummaryCell";
import { useSx } from "./sx";
import { Table } from "./Table";
import { Material, Row } from "./types";

interface Props {
  planId: PlanId;
  period: Period;
  disableEdit: boolean;
  rows: Row[];
  materials: Material[];
  focusedSteelGradeId: number;
  selectedSteelGradeIds: number[];
  onSteelGradeClick: (id: number) => void;
  onChange: (rows: Row[]) => void;
  mixGrouping: MixGrouping;
  hasDri: boolean;
  columnGroupingModel: GridColumnGroup[];
  preloadedSummaries?: Record<number, SingleMixSummary | null | undefined>;
  preloadedDris?: Record<number, MixMaterial | null | undefined>;
}

const useColumns = (
  materials: Material[],
  focusedSteelGradeId: number,
  selectedSteelGradeIds: number[],
  disableEdit: boolean,
  mixGrouping: MixGrouping,
  hasDri: boolean,
  onSteelGradeClick: (id: number) => void,
  preloadedSummaries?: Record<number, SingleMixSummary | null | undefined>,
  preloadedDris?: Record<number, MixMaterial | null | undefined>
) => {
  const bracketUnits = useUnitsFormatter(true);
  const units = useUnitsFormatter(false);
  const { t } = useTenantTranslation();
  const { default_mix_breakdown, small_mass_per_mass, rounding_units } =
    useTenantData();
  const massScaleFactor =
    default_mix_breakdown === "small_mass" ? small_mass_per_mass : 1;
  const massUnit =
    default_mix_breakdown === "small_mass" ? "small_mass" : "mass";
  const materialFormat = useFormatNumber()(3, false);
  const totalFormat = useFormatNumber()(0, true);
  const groupingColumn = React.useMemo(() => {
    switch (mixGrouping) {
      case "copper_group":
        return copperGroup;
      case "grouping_name":
        return subStandardGroup(t);
    }
  }, [mixGrouping, t]);
  const summaryDetails: Detail[] = React.useMemo(() => {
    switch (mixGrouping) {
      case "copper_group":
        return ["price_diff", "cu"];
      case "grouping_name":
        return ["price_diff"];
    }
  }, [mixGrouping]);

  const columns = React.useMemo(() => {
    return [
      mixSummary(units, summaryDetails, preloadedSummaries),
      numHeats(),
      plotSelectSteelGrades(
        t,
        210,
        selectedSteelGradeIds,
        focusedSteelGradeId,
        onSteelGradeClick,
        preloadedSummaries
      ),
      groupingColumn,
      ...(hasDri
        ? [driWeight(t, massUnit, massScaleFactor, bracketUnits, preloadedDris)]
        : []),
      basketWeight(
        t,
        massUnit,
        massScaleFactor,
        totalFormat,
        bracketUnits,
        disableEdit,
        massUnit
      ),
      ...materials.map((material): GridColDef<Row> =>
        materialColumn(
          material,
          massScaleFactor,
          disableEdit,
          materialFormat,
          rounding_units
        )
      ),
    ];
  }, [
    materials,
    selectedSteelGradeIds,
    focusedSteelGradeId,
    disableEdit,
    summaryDetails,
    groupingColumn,
    hasDri,
    massUnit,
    massScaleFactor,
    bracketUnits,
    totalFormat,
    onSteelGradeClick,
    preloadedSummaries,
    preloadedDris,
    materialFormat,
    t,
    units,
    rounding_units,
  ]);
  return columns;
};

export const PopoverMixTable = ({
  disableEdit,
  materials,
  selectedSteelGradeIds,
  focusedSteelGradeId,
  onSteelGradeClick,
  rows,
  onChange,
  planId,
  period,
  mixGrouping,
  hasDri,
  columnGroupingModel,
  preloadedSummaries,
  preloadedDris,
}: Props) => {
  const columns = useColumns(
    materials,
    focusedSteelGradeId,
    selectedSteelGradeIds,
    disableEdit,
    mixGrouping,
    hasDri,
    onSteelGradeClick,
    preloadedSummaries,
    preloadedDris
  );

  const pinnedColumns = React.useMemo(() => {
    return columns.slice(0, hasDri ? 6 : 5).map((column) => column.field);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- hasDri is already captured indirectly via columns which is derived from it
  }, [columns]);

  const filterModel = useFilterModel(planId, period, "deploy");
  const sx = useSx();

  return (
    <Table
      filterModel={filterModel}
      onChange={onChange}
      rows={rows}
      columnGroupingModel={columnGroupingModel}
      columns={columns}
      disableEdit={disableEdit}
      pinnedColumns={pinnedColumns}
      sx={sx}
      columnVisibilityModel={{}}
    />
  );
};
